import { trapFocus, removeTrapFocus, lockScroll, unlockScroll } from '@archetype-themes/utils/a11y'
import boostSearchClient, { BoostAbortError, BOOST_SEARCH_CONFIG } from '@archetype-themes/utils/boost-search-client'
import { formatMoney } from '@archetype-themes/utils/currency'

const DEFAULT_PAGE = 1
const DEFAULT_SORT = BOOST_SEARCH_CONFIG.searchDefaults.sort || 'relevance'
const DEFAULT_LIMIT = BOOST_SEARCH_CONFIG.searchDefaults.limit || BOOST_SEARCH_CONFIG.pageSize || 24
const PAGINATION_WINDOW = 2
/* ------------------------------------------------------------------
   HORANDO 17.09.2026 - Der Filter der Suchergebnisseite ist jetzt
   derselbe wie auf den Kollektionsseiten:

   - oben die Leiste FILTER (n) | N Ergebnisse | SORTIEREN,
   - ab 1000px darunter die offene Facettenleiste (.plp-facets) mit
     Marke und Modell an erster Stelle,
   - darunter auf dem Telefon Marke und Modell als zwei Knoepfe, alle
     uebrigen Facetten im Vollbild-Panel hinter FILTER.

   Vorher stand hier das Tab-Popup von Boost - eine zweite, voellig
   andere Bedienung fuer dieselbe Aufgabe. Wer von einer Markenseite
   in die Suche wechselte, bekam ohne Not ein fremdes Fenster.

   Die Marke war bisher ausgeschlossen, weil auf einer
   Kollektionsseite die Marke schon die Seite ist. Auf der Suchseite
   gibt es diese Seite nicht - dort ist die Marke die wichtigste
   Facette ueberhaupt und steht deshalb vorne. Preis und Produktart
   bleiben draussen: den Preis filtert Search & Discovery, die
   Produktart sagt bei Uhren nichts.
   ------------------------------------------------------------------ */
const EXCLUDED_FILTER_OPTION_IDS = new Set(['pf_pt_product_type', 'pf_p_price'])
/* Boost benennt die Markenfacette nicht ueberall gleich - je nach
   Einrichtung kommt sie als Lieferant (pf_v_vendor) oder als
   Metafeld mit eigener Kennung. Deshalb zaehlt hier zusaetzlich die
   Beschriftung; dasselbe gilt fuer das Modell. */
const BRAND_FILTER_OPTION_ID = 'pf_v_vendor'
const BRAND_FILTER_LABELS = ['marke', 'brand', 'vendor', 'marque', 'marca', 'merk']
const MODEL_FILTER_LABELS = ['modell', 'model', 'modele', 'modèle', 'modelo']
const FACET_BREAKPOINT = '(min-width: 1000px)'
const YEAR_ON_STOCK_ONLY_VENDORS = ['Audemars Piguet'] /* 23.09.2026: Rolex und Patek Philippe zeigen das Jahr wieder */
/* HORANDO 19.09.2026 - Marken, bei denen der durchgestrichene UVP gar
   nicht mehr gezeigt wird. Dieselbe Regel wie in
   snippets/hd-show-uvp.liquid, dort steht die Begruendung. Wer sie
   aendert, muss beide Stellen nachziehen und assets/quick-search-overlay.js
   dazu. */
const UVP_HIDDEN_VENDORS = ['Audemars Piguet', 'Rolex', 'Patek Philippe']

const SORT_OPTIONS = [
  {
    labelKey: 'sortRelevance',
    value: 'relevance'
  },
  {
    labelKey: 'sortPriceAscending',
    value: 'price-ascending'
  },
  {
    labelKey: 'sortPriceDescending',
    value: 'price-descending'
  },
  {
    labelKey: 'sortNewest',
    value: 'created-descending'
  }
]

export function normalizeQuery(value) {
  return String(value || '').replace(/\s+/g, ' ').trim()
}

export function parsePageValue(value) {
  const parsedValue = Number.parseInt(value, 10)

  if (!Number.isFinite(parsedValue) || parsedValue < DEFAULT_PAGE) {
    return DEFAULT_PAGE
  }

  return parsedValue
}

export function priceToCents(price) {
  const parsedPrice = Number.parseFloat(price)

  if (!Number.isFinite(parsedPrice)) {
    return null
  }

  return Math.round(parsedPrice * 100)
}

export function stripHtmlTags(value) {
  return String(value || '').replace(/<[^>]*>/g, '')
}

export function formatMoneyAmount(amount, meta, moneyFormat) {
  if (moneyFormat) {
    return formatMoney(amount, moneyFormat, false)
  }

  const boostMoneyFormat = meta?.money_format_with_currency || meta?.money_format || '${{amount}}'
  return formatMoney(amount, stripHtmlTags(boostMoneyFormat), false)
}

export function variantMatchesQuery(variant, normalizedQuery) {
  if (!normalizedQuery || !variant) {
    return false
  }

  const sku = variant.sku ? String(variant.sku).toLowerCase() : ''
  const title = variant.title ? String(variant.title).toLowerCase() : ''

  return sku.includes(normalizedQuery) || title.includes(normalizedQuery)
}

export function selectVariant(variants, query) {
  if (!Array.isArray(variants) || !variants.length) {
    return null
  }

  const normalizedQuery = normalizeQuery(query).toLowerCase()
  const availableVariants = variants.filter((variant) => variant && variant.available)
  const matchingAvailableVariant = availableVariants.find((variant) =>
    variantMatchesQuery(variant, normalizedQuery)
  )

  if (matchingAvailableVariant) {
    return matchingAvailableVariant
  }

  if (availableVariants.length) {
    return availableVariants[0]
  }

  return variants[0]
}

export function isAvailable(product, variant) {
  if (variant && typeof variant.available === 'boolean') {
    return variant.available
  }

  return Boolean(product?.available)
}

export function getValidHandle(handle) {
  const normalizedHandle = String(handle || '').trim()

  if (!normalizedHandle) {
    return ''
  }

  if (!/^[a-z0-9-]+$/i.test(normalizedHandle)) {
    return ''
  }

  return normalizedHandle
}

export function buildResourceUrl(kind, handle, routesRoot = '/') {
  const validHandle = getValidHandle(handle)

  if (!validHandle) {
    return ''
  }

  return `${normalizeRoutesRoot(routesRoot)}${kind}/${encodeURIComponent(validHandle)}`
}

export function buildSearchPageUrl({
  filters = {},
  page = DEFAULT_PAGE,
  query,
  searchPath,
  sort = DEFAULT_SORT
}) {
  const normalizedQuery = normalizeQuery(query)
  const normalizedPage = parsePageValue(page)
  const normalizedSort = normalizeSortValue(sort)
  const url = new URL(searchPath, window.location.origin)

  if (normalizedQuery) {
    url.searchParams.set('q', normalizedQuery)
  }

  if (normalizedSort !== DEFAULT_SORT) {
    url.searchParams.set('sort', normalizedSort)
  }

  if (normalizedPage !== DEFAULT_PAGE) {
    url.searchParams.set('page', String(normalizedPage))
  }

  appendFilterSearchParams(url.searchParams, filters)

  return `${url.pathname}${url.search}`
}

function normalizeRoutesRoot(routesRoot) {
  const normalizedRoot = String(routesRoot || '/').trim()

  if (!normalizedRoot) {
    return '/'
  }

  return normalizedRoot.endsWith('/') ? normalizedRoot : `${normalizedRoot}/`
}

function normalizeSortValue(value) {
  return SORT_OPTIONS.some((option) => option.value === value) ? value : DEFAULT_SORT
}

function getPaginationParts(currentPage, totalPages) {
  const safeTotalPages = Math.max(DEFAULT_PAGE, totalPages)
  const pages = new Set([1, safeTotalPages])

  for (
    let page = Math.max(DEFAULT_PAGE, currentPage - PAGINATION_WINDOW);
    page <= Math.min(safeTotalPages, currentPage + PAGINATION_WINDOW);
    page += 1
  ) {
    pages.add(page)
  }

  const sortedPages = Array.from(pages).sort((left, right) => left - right)
  const parts = []

  sortedPages.forEach((page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      parts.push({
        type: 'gap'
      })
    }

    parts.push({
      page,
      type: 'page'
    })
  })

  return parts
}

function appendFilterSearchParams(searchParams, filters) {
  Object.entries(filters || {}).forEach(([filterOptionId, values]) => {
    normalizeFilterValues(values).forEach((value) => {
      searchParams.append(`${filterOptionId}[]`, value)
    })
  })
}

function normalizeFilterValues(values) {
  if (!Array.isArray(values)) {
    return []
  }

  return Array.from(
    new Set(
      values
        .map((value) => normalizeQuery(value))
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right))
}

function filtersAreEqual(leftFilters, rightFilters, filterConfigs) {
  return filterConfigs.every((config) => {
    const leftValues = normalizeFilterValues(leftFilters?.[config.optionId])
    const rightValues = normalizeFilterValues(rightFilters?.[config.optionId])

    if (leftValues.length !== rightValues.length) {
      return false
    }

    return leftValues.every((value, index) => value === rightValues[index])
  })
}

function sanitizeIdentifier(value) {
  return String(value || '').replace(/[^a-z0-9_-]+/gi, '-')
}

function createFilterTabId(value) {
  return sanitizeIdentifier(String(value || '').toLowerCase())
}

function getLastColonSegment(value) {
  const normalizedValue = normalizeQuery(value)

  if (!normalizedValue) {
    return ''
  }

  const parts = normalizedValue.split(':')
  return parts[parts.length - 1] || normalizedValue
}

function stripLocaleTag(label) {
  return normalizeQuery(label).replace(/^\[[^\]]+\]\s*/u, '')
}

function getOptionLocale(option) {
  const labelMatch = normalizeQuery(option?.label).match(/^\[([^\]]+)\]/u)

  if (labelMatch) {
    const normalizedLocale = labelMatch[1].toLowerCase().match(/[a-z]{2}/u)
    return normalizedLocale ? normalizedLocale[0] : labelMatch[1].toLowerCase()
  }

  const optionIdMatch = normalizeQuery(option?.filterOptionId).match(/^pf_[^_]+_([a-z]{2})_/iu)

  if (optionIdMatch) {
    return optionIdMatch[1].toLowerCase()
  }

  return ''
}

function getOptionFamilyKey(option) {
  const optionId = normalizeQuery(option?.filterOptionId)
  const optionIdMatch = optionId.match(/^(pf_[^_]+)_([a-z]{2})_(.+)$/iu)

  if (optionIdMatch) {
    return `${optionIdMatch[1]}_${optionIdMatch[3]}`
  }

  return optionId
}

/**
 * Reihenfolge der Facettenknoepfe: Marke, dann Modell, dann alles
 * uebrige in der Reihenfolge, die Boost liefert. Array.sort ist stabil,
 * die dritte Gruppe behaelt ihre Position also unveraendert.
 */
function facetRank(config) {
  if (config.isBrand) {
    return 0
  }

  if (config.isModel) {
    return 1
  }

  return 2
}

/**
 * Manche Wertelisten wiederholen den Facettennamen im Wert selbst:
 * unter "Lieferzeit" stand "Lieferzeit 2-4 Wochen". Im Knopf darueber
 * steht der Name schon, das Wort davor sagt also nichts. Es faellt weg,
 * aber nur wenn danach noch etwas uebrig bleibt.
 */
function stripFacetPrefix(label, facetLabel) {
  const value = String(label || '')
  const prefix = `${String(facetLabel || '')} `

  if (prefix.length > 1 && value.length > prefix.length && value.slice(0, prefix.length) === prefix) {
    return value.slice(prefix.length)
  }

  return value
}

class BoostSearchResults extends HTMLElement {
  connectedCallback() {
    if (this.initialized) {
      return
    }

    this.initialized = true
    this.requestController = null
    this.requestGeneration = 0
    this.hasHydratedResults = false
    this.popupOpen = false
    this.popupContext = null
    this.pendingFilters = null
    this.popupSnapshotFilters = null
    this.popupTrigger = null
    this.handleClick = this.handleClick.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handlePopState = this.handlePopState.bind(this)

    this.root = this.querySelector('[data-boost-search-results-root]')
    this.fallback = this.querySelector('[data-boost-search-results-fallback]')
    this.desktopFilterTrigger = this.querySelector('[data-boost-filter-trigger="desktop"]')
    this.mobileFilterTrigger = this.querySelector('[data-boost-filter-trigger="mobile"]')
    this.popupShells = {
      mobile: this.querySelector('[data-boost-filter-popup-shell="mobile"]')
    }
    this.popups = {
      mobile: this.querySelector('[data-boost-filter-popup="mobile"]')
    }

    // Leiste, Trefferzahl, Sortierung und Facettenknoepfe stehen als
    // leeres Geruest im Liquid und wandern bei jedem Neuaufbau als
    // ganzer Block an den Anfang der Ergebnisliste.
    this.filterControls = this.querySelector('[data-boost-controls]')
    this.resultsCountNode = this.querySelector('[data-boost-results-count]')
    this.sortHost = this.querySelector('[data-boost-sort-host]')
    this.facetBar = this.querySelector('[data-boost-facets]')
    this.facetRow = this.querySelector('[data-boost-facets-row]')
    this.facetChips = this.querySelector('[data-boost-facets-active]')

    // Welches Aufklappmenue offen war, damit es nach dem Neuladen der
    // Treffer wieder offen steht - sonst muesste man fuer jeden zweiten
    // Wert derselben Facette neu aufklappen.
    this.openFacetId = ''
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener('click', this.handleDocumentClick)
    this.searchPath = this.dataset.searchUrl || window.Shopify?.routes?.search_url || '/search'
    this.routesRoot = this.dataset.routesRoot || window.Shopify?.routes?.root || '/'
    this.moneyFormat = this.dataset.moneyFormat || ''
    this.conciergeBadgeLabel = this.dataset.conciergeBadgeLabel || ''
    this.pageSize = parsePageValue(this.dataset.pageSize || DEFAULT_LIMIT)
    this.gridView = this.dataset.gridView || 'medium'
    this.gridExtraClass = this.dataset.gridExtraClass || ''
    this.enableSort = this.dataset.enableSort === 'true'
    this.locale = normalizeQuery(this.dataset.locale).toLowerCase()
    this.i18n = this.getI18n()
    this.hasResolvedFilterConfigs = false
    this.filterConfigs = this.buildFilterConfigs()

    this.addEventListener('click', this.handleClick)
    this.addEventListener('change', this.handleChange)
    this.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('popstate', this.handlePopState)

    const state = this.getStateFromLocation()

    if (!state.query) {
      this.currentState = state
      this.showFallback()
      return
    }

    this.fetchAndRender(state, {
      historyMode: 'replace'
    })
  }

  disconnectedCallback() {
    this.abortRequest()
    this.closePopup({ restoreFocus: false, discardPending: true })
    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('change', this.handleChange)
    this.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('popstate', this.handlePopState)
    document.removeEventListener('click', this.handleDocumentClick)
  }

  /**
   * Klick irgendwo sonst auf der Seite schliesst ein offenes
   * Facettenmenue - wie auf der Kollektionsseite.
   */
  handleDocumentClick(event) {
    if (this.contains(event.target)) {
      return
    }

    this.closeFacetMenus()
    this.closeSortList()
  }

  getI18n() {
    return {
      clearAll: this.dataset.clearAllLabel || 'Clear all',
      delivery: this.dataset.deliveryLabel || 'Lieferzeit',
      deliveryDays: this.dataset.deliveryDaysLabel || 'Tage',
      deliveryOnRequest: this.dataset.onRequestLabel || 'Auf Anfrage',
      deliveryWeeks: this.dataset.deliveryWeeksLabel || 'Wochen',
      didYouMean: this.dataset.didYouMeanLabel || 'Did you mean',
      filter: this.dataset.filterLabel || 'Filter',
      applyFilters: this.dataset.applyFiltersLabel || 'Apply filters',
      forQuery: this.dataset.forQueryLabel || 'for "{{ query }}"',
      immediatelyAvailable: this.dataset.immediatelyAvailableLabel || 'Sofort lieferbar',
      fastDelivery: this.dataset.fastDeliveryLabel || 'Schnell lieferbar',
      next: this.dataset.nextLabel || 'Next',
      noImage: this.dataset.noImageLabel || 'No image',
      noResultsCopy: this.dataset.noResultsCopy || 'No live results matched "{{ query }}".',
      noResultsTitle: this.dataset.noResultsTitle || 'No results found',
      previous: this.dataset.previousLabel || 'Previous',
      priceUnavailable: this.dataset.priceUnavailableLabel || 'Price unavailable',
      productImageAlt: this.dataset.productImageAlt || 'Product image',
      resultsCountOne: this.dataset.resultsCountOne || '__count__ result',
      resultsCountOther: this.dataset.resultsCountOther || '__count__ results',
      savePrice: this.dataset.savePriceLabel || 'You save',
      searchError: this.dataset.searchErrorLabel || 'Search is temporarily unavailable.',
      soldOut: this.dataset.soldOutLabel || 'Sold Out',
      sortBy: this.dataset.sortByLabel || 'Sort by',
      sortNewest: this.dataset.sortNewestLabel || 'Newest',
      sortPriceAscending: this.dataset.sortPriceAscendingLabel || 'Price, low to high',
      sortPriceDescending: this.dataset.sortPriceDescendingLabel || 'Price, high to low',
      sortRelevance: this.dataset.sortRelevanceLabel || 'Relevance',
      unknownBrand: this.dataset.unknownBrandLabel || 'Unknown brand',
      untitledProduct: this.dataset.untitledProductLabel || 'Untitled product',
      uvp: this.dataset.uvpLabel || 'UVP',
      year: this.dataset.yearLabel || 'Year'
    }
  }

  buildFilterConfigs(response) {
    const options = Array.isArray(response?.filter?.options) ? response.filter.options : []
    const sortedOptions = [...options].sort((left, right) => {
      return Number(left?.position || 0) - Number(right?.position || 0)
    })
    const optionFamilies = sortedOptions.reduce((families, option) => {
      const optionId = normalizeQuery(option?.filterOptionId)
      const optionLocale = getOptionLocale(option)

      if (!optionId || EXCLUDED_FILTER_OPTION_IDS.has(optionId)) {
        return families
      }

      if (option?.status === 'disabled' || !Array.isArray(option?.values) || !option.values.length) {
        return families
      }

      const familyKey = getOptionFamilyKey(option)

      if (!families.has(familyKey)) {
        families.set(familyKey, new Set())
      }

      if (optionLocale) {
        families.get(familyKey).add(optionLocale)
      }

      return families
    }, new Map())

    return sortedOptions.reduce((configs, option) => {
      const optionId = normalizeQuery(option?.filterOptionId)
      const label = stripLocaleTag(option?.label)
      const optionLocale = getOptionLocale(option)
      const familyKey = getOptionFamilyKey(option)
      const familyLocales = optionFamilies.get(familyKey) || new Set()

      if (!optionId || EXCLUDED_FILTER_OPTION_IDS.has(optionId)) {
        return configs
      }

      if (option?.status === 'disabled' || !Array.isArray(option?.values) || !option.values.length) {
        return configs
      }

      if (optionLocale && familyLocales.size > 1 && optionLocale !== this.locale) {
        return configs
      }

      if (!label) {
        return configs
      }

      const normalizedLabel = label.trim().toLowerCase()

      configs.push({
        displayValue: getLastColonSegment,
        isBrand: optionId === BRAND_FILTER_OPTION_ID || BRAND_FILTER_LABELS.indexOf(normalizedLabel) !== -1,
        isModel: MODEL_FILTER_LABELS.indexOf(normalizedLabel) !== -1,
        label,
        optionId,
        tabId: createFilterTabId(optionId)
      })

      return configs
    }, []).sort((left, right) => facetRank(left) - facetRank(right))
  }

  getStateFromLocation() {
    const searchParams = new URLSearchParams(window.location.search)
    const query = normalizeQuery(searchParams.get('q') || this.dataset.initialQuery || '')

    return {
      filters: this.getFiltersFromSearchParams(searchParams),
      page: parsePageValue(searchParams.get('page') || DEFAULT_PAGE),
      query,
      sort: normalizeSortValue(searchParams.get('sort') || DEFAULT_SORT)
    }
  }

  getFiltersFromSearchParams(searchParams) {
    const filters = {}

    for (const [key, value] of searchParams.entries()) {
      if (!key.endsWith('[]')) {
        continue
      }

      const optionId = key.slice(0, -2)

      if (!optionId.startsWith('pf_')) {
        continue
      }

      if (!filters[optionId]) {
        filters[optionId] = []
      }

      filters[optionId].push(value)
    }

    return Object.entries(filters).reduce((result, [optionId, values]) => {
      result[optionId] = normalizeFilterValues(values)
      return result
    }, {})
  }

  normalizeFilters(filters) {
    if (!this.hasResolvedFilterConfigs && !this.filterConfigs.length) {
      return Object.entries(filters || {}).reduce((result, [optionId, values]) => {
        const normalizedOptionId = normalizeQuery(optionId)

        if (!normalizedOptionId.startsWith('pf_')) {
          return result
        }

        result[normalizedOptionId] = normalizeFilterValues(values)
        return result
      }, {})
    }

    return this.filterConfigs.reduce((result, config) => {
      result[config.optionId] = normalizeFilterValues(filters?.[config.optionId])
      return result
    }, {})
  }

  createEmptyFilterState() {
    return this.filterConfigs.reduce((result, config) => {
      result[config.optionId] = []
      return result
    }, {})
  }

  handleClick(event) {
    const filterTrigger = event.target.closest('[data-boost-filter-trigger]')

    if (filterTrigger) {
      event.preventDefault()
      this.openPopup(filterTrigger.getAttribute('data-boost-filter-trigger'), filterTrigger)
      return
    }

    // ---- Facettenleiste (Desktop und die zwei Knoepfe auf dem Telefon)
    const facetButton = event.target.closest('[data-boost-facet-button]')

    if (facetButton) {
      event.preventDefault()
      event.stopPropagation()
      this.closeSortList()
      this.toggleFacetMenu(facetButton.getAttribute('data-boost-facet-button'))
      return
    }

    const facetValue = event.target.closest('[data-boost-facet-value]')

    if (facetValue) {
      event.preventDefault()

      const filterOptionId = facetValue.getAttribute('data-filter-option-id') || ''
      const rawValue = facetValue.getAttribute('data-boost-facet-value') || ''

      if (!filterOptionId || !rawValue) {
        return
      }

      // In der offenen Leiste wirkt jeder Wert sofort - wie der Link auf
      // der Kollektionsseite. Das Menue bleibt offen, damit man einen
      // zweiten Wert derselben Facette nicht neu aufklappen muss.
      this.openFacetId = filterOptionId

      this.fetchAndRender(
        {
          ...this.currentState,
          filters: this.updateFilterValue(
            this.currentState.filters,
            filterOptionId,
            rawValue,
            !facetValue.classList.contains('is-active')
          ),
          page: DEFAULT_PAGE
        },
        {
          historyMode: 'push'
        }
      )

      return
    }

    const sortButton = event.target.closest('[data-boost-sort-button]')

    if (sortButton) {
      event.preventDefault()
      event.stopPropagation()
      this.closeFacetMenus()

      const sortList = this.sortHost?.querySelector('[data-boost-sort-list]')

      if (sortList) {
        const willOpen = sortList.hidden
        sortList.hidden = !willOpen
        sortButton.setAttribute('aria-expanded', willOpen ? 'true' : 'false')
      }

      return
    }

    const sortOption = event.target.closest('[data-boost-sort-option]')

    if (sortOption) {
      event.preventDefault()
      this.closeSortList()

      this.fetchAndRender(
        {
          ...this.currentState,
          page: DEFAULT_PAGE,
          sort: normalizeSortValue(sortOption.getAttribute('data-boost-sort-option'))
        },
        {
          historyMode: 'push'
        }
      )

      return
    }

    const popupClose = event.target.closest('[data-boost-filter-popup-close], [data-boost-filter-popup-backdrop]')

    if (popupClose) {
      event.preventDefault()
      this.closePopup()
      return
    }

    const popupBack = event.target.closest('[data-boost-filter-back]')

    if (popupBack) {
      event.preventDefault()
      this.setPopupTab(popupBack.closest('[data-boost-filter-popup]'), '')
      return
    }

    const popupTab = event.target.closest('[data-boost-filter-tab]')

    if (popupTab) {
      event.preventDefault()
      this.setPopupTab(
        popupTab.closest('[data-boost-filter-popup]'),
        popupTab.getAttribute('data-tab-id')
      )
      return
    }

    const applyButton = event.target.closest('[data-boost-filter-apply]')

    if (applyButton) {
      event.preventDefault()

      if (!applyButton.disabled) {
        this.applyPendingFilters()
      }

      return
    }

    const removeFilterButton = event.target.closest('[data-boost-filter-remove]')

    if (removeFilterButton) {
      event.preventDefault()
      this.fetchAndRender(
        {
          ...this.currentState,
          filters: this.removeFilterValue(
            this.currentState.filters,
            removeFilterButton.getAttribute('data-filter-option-id'),
            removeFilterButton.getAttribute('data-filter-value')
          ),
          page: DEFAULT_PAGE
        },
        {
          historyMode: 'push'
        }
      )
      return
    }

    const clearAllButton = event.target.closest('[data-boost-filter-clear-all]')

    if (clearAllButton) {
      event.preventDefault()
      this.fetchAndRender(
        {
          ...this.currentState,
          filters: this.createEmptyFilterState(),
          page: DEFAULT_PAGE
        },
        {
          historyMode: 'push'
        }
      )
      return
    }

    const link = event.target.closest('[data-boost-search-link]')

    if (!link) {
      return
    }

    event.preventDefault()

    if (link.hasAttribute('data-boost-page-link')) {
      const page = parsePageValue(link.getAttribute('data-page'))

      this.fetchAndRender(
        {
          ...this.currentState,
          page
        },
        {
          historyMode: 'push'
        }
      )

      return
    }

    if (link.hasAttribute('data-boost-dym-link')) {
      const query = normalizeQuery(link.getAttribute('data-query'))

      if (!query) {
        return
      }

      this.fetchAndRender(
        {
          filters: this.createEmptyFilterState(),
          page: DEFAULT_PAGE,
          query,
          sort: DEFAULT_SORT
        },
        {
          historyMode: 'push'
        }
      )
    }
  }

  handleChange(event) {
    const checkbox = event.target.closest('[data-boost-filter-input]')

    if (checkbox) {
      if (!this.popupOpen || !this.pendingFilters) {
        return
      }

      const filterOptionId = checkbox.getAttribute('data-filter-option-id') || ''
      const rawValue = checkbox.value

      if (!filterOptionId || !rawValue) {
        return
      }

      this.pendingFilters = this.updateFilterValue(
        this.pendingFilters,
        filterOptionId,
        rawValue,
        checkbox.checked
      )

      this.syncPopupSelections()
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') {
      return
    }

    if (this.popupOpen) {
      event.preventDefault()
      this.closePopup()
      return
    }

    this.closeFacetMenus()
    this.closeSortList()
  }

  handlePopState() {
    const state = this.getStateFromLocation()

    if (!state.query) {
      this.currentState = state
      this.showFallback()
      return
    }

    this.fetchAndRender(state, {
      historyMode: 'none'
    })
  }

  async fetchAndRender(state, options = {}) {
    const nextState = {
      filters: this.normalizeFilters(state.filters || this.createEmptyFilterState()),
      page: parsePageValue(state.page),
      query: normalizeQuery(state.query),
      sort: normalizeSortValue(state.sort)
    }

    if (!nextState.query) {
      this.currentState = nextState
      this.showFallback()
      return
    }

    const requestGeneration = this.requestGeneration + 1
    this.requestGeneration = requestGeneration
    this.abortRequest()
    this.requestController = new AbortController()
    this.currentState = nextState

    this.setBusy(true)

    try {
      const response = await boostSearchClient.filter(
        {
          limit: this.pageSize,
          page: nextState.page,
          params: this.buildBoostFilterParams(nextState.filters),
          query: nextState.query,
          sort: nextState.sort
        },
        this.requestController.signal
      )

      if (requestGeneration !== this.requestGeneration) {
        return
      }

      this.filterConfigs = this.buildFilterConfigs(response)
      this.hasResolvedFilterConfigs = true
      const reconciledState = {
        ...nextState,
        filters: this.normalizeFilters(nextState.filters || this.createEmptyFilterState())
      }

      this.response = response
      this.currentState = reconciledState
      this.renderFilterUi(response, reconciledState)
      this.renderResponse(response, reconciledState)
      this.hasHydratedResults = true
      this.showHydratedResults()

      if (Number.parseInt(response?.total_product, 10) === 0) {
        this.fetchDidYouMeanHint(nextState.query, requestGeneration)
      }

      if (options.historyMode === 'push') {
        history.pushState(reconciledState, '', buildSearchPageUrl({
          filters: reconciledState.filters,
          page: reconciledState.page,
          query: reconciledState.query,
          searchPath: this.searchPath,
          sort: reconciledState.sort
        }))
      } else if (options.historyMode === 'replace') {
        history.replaceState(reconciledState, '', buildSearchPageUrl({
          filters: reconciledState.filters,
          page: reconciledState.page,
          query: reconciledState.query,
          searchPath: this.searchPath,
          sort: reconciledState.sort
        }))
      }
    } catch (error) {
      if (error instanceof BoostAbortError || requestGeneration !== this.requestGeneration) {
        return
      }

      console.error('Boost search results request failed', error)

      if (!this.hasHydratedResults) {
        this.showFallback()
        return
      }

      this.renderErrorBanner()
      this.showHydratedResults()
    } finally {
      if (requestGeneration === this.requestGeneration) {
        this.setBusy(false)
      }
    }
  }

  abortRequest() {
    if (this.requestController) {
      this.requestController.abort()
      this.requestController = null
    }
  }

  setBusy(isBusy) {
    if (this.root) {
      this.root.setAttribute('aria-busy', isBusy ? 'true' : 'false')
    }

    this.dataset.loading = isBusy ? 'true' : 'false'
  }

  showFallback() {
    if (this.root) {
      if (this.filterControls && this.filterControlsHome) {
        this.filterControlsHome.appendChild(this.filterControls)
      }

      this.root.hidden = true
      this.root.replaceChildren()
    }

    Object.values(this.popups).forEach((popup) => {
      if (popup) {
        popup.replaceChildren()
        popup.hidden = true
      }
    })

    if (this.desktopFilterTrigger) {
      this.desktopFilterTrigger.hidden = true
    }

    if (this.mobileFilterTrigger) {
      this.mobileFilterTrigger.hidden = true
    }

    this.closePopup({ restoreFocus: false, discardPending: true })

    if (this.fallback) {
      this.fallback.hidden = false
    }
  }

  showHydratedResults() {
    if (this.root) {
      this.root.hidden = false
    }

    if (this.fallback) {
      this.fallback.hidden = true
    }
  }

  renderResponse(response, state) {
    if (!this.root) {
      return
    }

    const fragment = document.createDocumentFragment()

    if (this.filterControls) {
      this.filterControls.hidden = false
      fragment.appendChild(this.filterControls)
    }

    fragment.appendChild(this.buildSummary())
    fragment.appendChild(this.buildBody(response, state))
    fragment.appendChild(this.buildPagination(response, state))

    this.root.replaceChildren(fragment)
  }

  renderFilterUi(response, state) {
    const availableOptions = this.getRenderableFilterOptions(response)
    const hasFilters = availableOptions.length > 0

    // Marke und Modell stehen immer in der Leiste - auf dem Telefon als
    // die beiden Knoepfe darueber, auf dem Desktop als die ersten zwei
    // Facetten. Im Panel hinter FILTER stehen sie deshalb nicht noch
    // einmal; genau so haelt es die Kollektionsseite.
    const panelOptions = availableOptions.filter(({ config }) => !config.isBrand && !config.isModel)

    this.renderFacetBar(availableOptions, state)
    this.renderFilterPopup('mobile', panelOptions, response, state)
    this.updateFilterTriggerLabels(state.filters)
    this.updateResultsCount(response)
    this.renderSortControl(state.sort)

    if (this.mobileFilterTrigger) {
      this.mobileFilterTrigger.hidden = panelOptions.length === 0
    }

    if (!hasFilters) {
      this.closePopup({ restoreFocus: false, discardPending: true })
    }
  }

  /**
   * Trefferzahl in der Leiste - dieselbe Angabe wie auf der
   * Kollektionsseite, ohne die Suchanfrage: die steht im Suchfeld und
   * in der Ueberschrift schon zweimal da.
   */
  updateResultsCount(response) {
    if (!this.resultsCountNode) {
      return
    }

    const count = Number.parseInt(response?.total_product, 10) || 0
    this.resultsCountNode.textContent = this.getResultsCountText(count)
    this.resultsCountNode.hidden = count === 0
  }

  /**
   * Die Sortierung als Aufklappliste im Stil der Kollektionsseite
   * (.plp-sort) statt des nativen Auswahlfeldes. Auf dem Telefon steht
   * in der Leiste nur das Wort SORTIEREN; die Regeln dazu stehen in
   * assets/horando-braun-mobil.css und greifen ueber dieselben Klassen.
   */
  renderSortControl(activeSort) {
    if (!this.sortHost || !this.enableSort) {
      return
    }

    const activeOption = SORT_OPTIONS.find((option) => option.value === activeSort) || SORT_OPTIONS[0]
    const wasOpen = this.sortHost.querySelector('[data-boost-sort-list]')?.hidden === false

    this.sortHost.replaceChildren()
    this.sortHost.className = 'plp-sort'

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'plp-sort__button'
    button.setAttribute('aria-expanded', wasOpen ? 'true' : 'false')
    button.setAttribute('data-boost-sort-button', '')

    // Dasselbe Zeichen wie in snippets/plp-sort.liquid - die beiden
    // Pfeile auf und ab. Aendert sich das eine, muss das andere mit.
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    icon.setAttribute('class', 'plp-sort__icon')
    icon.setAttribute('width', '14')
    icon.setAttribute('height', '14')
    icon.setAttribute('viewBox', '0 0 21 16')
    icon.setAttribute('fill', 'none')
    icon.setAttribute('aria-hidden', 'true')
    icon.setAttribute('focusable', 'false')
    const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    iconPath.setAttribute(
      'd',
      'M4.908.293A1.04 1.04 0 0 1 5.615 0c.265 0 .52.105.707.293l4 4a1 1 0 0 1-1.414 1.414L6.615 3.414V15a1 1 0 1 1-2 0V3.414L2.322 5.707A1 1 0 0 1 .908 4.293l4-4ZM14.615 12.586V1a1 1 0 1 1 2 0v11.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414l2.293 2.293Z'
    )
    iconPath.setAttribute('fill', 'currentColor')
    icon.appendChild(iconPath)
    button.appendChild(icon)

    const label = document.createElement('span')
    label.className = 'plp-sort__label'
    label.textContent = this.i18n.sortBy
    button.appendChild(label)

    const current = document.createElement('span')
    current.className = 'plp-sort__current'
    current.textContent = this.i18n[activeOption.labelKey]
    button.appendChild(current)

    const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    caret.setAttribute('class', 'plp-sort__caret')
    caret.setAttribute('width', '10')
    caret.setAttribute('height', '6')
    caret.setAttribute('viewBox', '0 0 10 6')
    caret.setAttribute('fill', 'none')
    caret.setAttribute('aria-hidden', 'true')
    caret.setAttribute('focusable', 'false')
    const caretPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    caretPath.setAttribute('d', 'm1 1 4 4 4-4')
    caretPath.setAttribute('stroke', 'currentColor')
    caretPath.setAttribute('stroke-width', '1.4')
    caretPath.setAttribute('fill', 'none')
    caret.appendChild(caretPath)
    button.appendChild(caret)

    this.sortHost.appendChild(button)

    const list = document.createElement('ul')
    list.className = 'plp-sort__list'
    list.hidden = !wasOpen
    list.setAttribute('data-boost-sort-list', '')

    SORT_OPTIONS.forEach((option) => {
      const item = document.createElement('li')
      const trigger = document.createElement('button')
      trigger.type = 'button'
      trigger.className = option.value === activeOption.value
        ? 'plp-sort__option is-active'
        : 'plp-sort__option'
      trigger.textContent = this.i18n[option.labelKey]
      trigger.setAttribute('data-boost-sort-option', option.value)
      item.appendChild(trigger)
      list.appendChild(item)
    })

    this.sortHost.appendChild(list)
  }

  /**
   * Die offene Facettenleiste. Ab 1000px stehen alle Facetten als
   * Knoepfe nebeneinander, darunter nur Marke und Modell - beides regelt
   * das CSS in snippets/section-main-search.liquid, hier entsteht in
   * beiden Faellen dasselbe Markup wie in snippets/plp-facets.liquid.
   */
  renderFacetBar(availableOptions, state) {
    if (!this.facetRow || !this.facetBar) {
      return
    }

    this.facetRow.replaceChildren()

    if (!availableOptions.length) {
      this.facetBar.hidden = true
      return
    }

    this.facetBar.hidden = false

    availableOptions.forEach(({ config, option }) => {
      const facet = this.buildFacet(config, option, state)

      if (facet) {
        this.facetRow.appendChild(facet)
      }
    })

    this.renderFacetChips(state)
  }

  buildFacet(config, option, state) {
    const activeValues = normalizeFilterValues(state.filters?.[config.optionId])
    const values = (Array.isArray(option?.values) ? option.values : []).filter((value) => {
      const rawValue = normalizeQuery(value?.key)
      return rawValue && (Number(value?.doc_count) > 0 || activeValues.includes(rawValue))
    })

    // Eine Facette mit einem einzigen waehlbaren Wert ist eine Auswahl
    // ohne Alternative - dieselbe Regel wie auf der Kollektionsseite.
    if (values.length < 2) {
      return null
    }

    const facet = document.createElement('div')
    facet.className = 'plp-facet'
    facet.setAttribute('data-boost-facet', config.optionId)

    if (activeValues.length) {
      facet.classList.add('plp-facet--active')
    }

    if (config.isBrand) {
      facet.classList.add('plp-facet--brand')
    }

    if (config.isModel) {
      facet.classList.add('plp-facet--model')
    }

    const isOpen = this.openFacetId === config.optionId

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'plp-facet__button'
    button.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
    button.setAttribute('data-boost-facet-button', config.optionId)

    const name = document.createElement('span')
    name.className = 'plp-facet__name'
    name.textContent = config.label
    button.appendChild(name)

    if (activeValues.length) {
      const badge = document.createElement('span')
      badge.className = 'plp-facet__badge'
      badge.textContent = String(activeValues.length)
      button.appendChild(badge)
    }

    const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    chevron.setAttribute('class', 'plp-facet__chevron')
    chevron.setAttribute('width', '9')
    chevron.setAttribute('height', '6')
    chevron.setAttribute('viewBox', '0 0 9 6')
    chevron.setAttribute('fill', 'none')
    chevron.setAttribute('aria-hidden', 'true')
    chevron.setAttribute('focusable', 'false')
    const chevronPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    chevronPath.setAttribute('d', 'M1 1L4.5 4.5L8 1')
    chevronPath.setAttribute('stroke', 'currentColor')
    chevronPath.setAttribute('stroke-width', '1.2')
    chevronPath.setAttribute('stroke-linecap', 'round')
    chevronPath.setAttribute('stroke-linejoin', 'round')
    chevron.appendChild(chevronPath)
    button.appendChild(chevron)

    facet.appendChild(button)

    const menu = document.createElement('div')
    menu.className = 'plp-facet__menu'
    menu.hidden = !isOpen
    menu.setAttribute('data-boost-facet-menu', '')

    const list = document.createElement('ul')
    list.className = 'plp-facet__list'

    values.forEach((value) => {
      list.appendChild(this.buildFacetValue(config, value, activeValues))
    })

    menu.appendChild(list)
    facet.appendChild(menu)

    return facet
  }

  buildFacetValue(config, value, activeValues) {
    const rawValue = normalizeQuery(value.key)
    const isActive = activeValues.includes(rawValue)

    const item = document.createElement('li')

    const trigger = document.createElement('button')
    trigger.type = 'button'
    trigger.className = isActive ? 'plp-facet__value is-active' : 'plp-facet__value'
    trigger.setAttribute('data-boost-facet-value', rawValue)
    trigger.setAttribute('data-filter-option-id', config.optionId)

    if (isActive) {
      trigger.setAttribute('aria-current', 'true')
    }

    const box = document.createElement('span')
    box.className = 'plp-facet__box'
    box.setAttribute('aria-hidden', 'true')
    const check = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    check.setAttribute('width', '10')
    check.setAttribute('height', '8')
    check.setAttribute('viewBox', '0 0 10 8')
    check.setAttribute('fill', 'none')
    check.setAttribute('focusable', 'false')
    const checkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    checkPath.setAttribute('d', 'M1 4L3.7 6.7L9 1.3')
    checkPath.setAttribute('stroke', 'currentColor')
    checkPath.setAttribute('stroke-width', '1.6')
    checkPath.setAttribute('stroke-linecap', 'round')
    checkPath.setAttribute('stroke-linejoin', 'round')
    check.appendChild(checkPath)
    box.appendChild(check)
    trigger.appendChild(box)

    const label = document.createElement('span')
    label.className = 'plp-facet__value-label'
    label.textContent = stripFacetPrefix(config.displayValue(rawValue), config.label)
    trigger.appendChild(label)

    const count = document.createElement('span')
    count.className = 'plp-facet__count'
    count.textContent = String(Number(value?.doc_count) || 0)
    trigger.appendChild(count)

    item.appendChild(trigger)

    return item
  }

  /**
   * Die gesetzten Filter als Chips unter der Leiste - ab 1000px, weil
   * darunter die Chipliste des Themes ueber den Uhren steht.
   */
  renderFacetChips(state) {
    if (!this.facetChips) {
      return
    }

    const activeEntries = this.getActiveFilterEntries(this.response, state)

    this.facetChips.replaceChildren()

    if (!activeEntries.length) {
      this.facetChips.hidden = true
      return
    }

    this.facetChips.hidden = false

    activeEntries.forEach((entry) => {
      const chip = document.createElement('button')
      chip.type = 'button'
      chip.className = 'plp-facets__chip'
      chip.setAttribute('data-boost-filter-remove', '')
      chip.setAttribute('data-filter-option-id', entry.filterOptionId)
      chip.setAttribute('data-filter-value', entry.rawValue)

      const label = document.createElement('span')
      label.textContent = entry.label
      chip.appendChild(label)

      const cross = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      cross.setAttribute('width', '9')
      cross.setAttribute('height', '9')
      cross.setAttribute('viewBox', '0 0 9 9')
      cross.setAttribute('fill', 'none')
      cross.setAttribute('aria-hidden', 'true')
      cross.setAttribute('focusable', 'false')
      const crossPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      crossPath.setAttribute('d', 'M1 1L8 8M8 1L1 8')
      crossPath.setAttribute('stroke', 'currentColor')
      crossPath.setAttribute('stroke-width', '1.3')
      crossPath.setAttribute('stroke-linecap', 'round')
      cross.appendChild(crossPath)
      chip.appendChild(cross)

      this.facetChips.appendChild(chip)
    })

    const clear = document.createElement('button')
    clear.type = 'button'
    clear.className = 'plp-facets__clear'
    clear.textContent = this.i18n.clearAll
    clear.setAttribute('data-boost-filter-clear-all', '')
    this.facetChips.appendChild(clear)
  }

  closeFacetMenus() {
    this.openFacetId = ''

    this.querySelectorAll('[data-boost-facet-button]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false')
    })

    this.querySelectorAll('[data-boost-facet-menu]').forEach((menu) => {
      menu.hidden = true
    })
  }

  toggleFacetMenu(optionId) {
    const shouldOpen = this.openFacetId !== optionId
    this.closeFacetMenus()

    if (!shouldOpen) {
      return
    }

    const facet = this.querySelector(`[data-boost-facet="${CSS.escape(optionId)}"]`)

    if (!facet) {
      return
    }

    this.openFacetId = optionId
    facet.querySelector('[data-boost-facet-button]')?.setAttribute('aria-expanded', 'true')

    const menu = facet.querySelector('[data-boost-facet-menu]')

    if (menu) {
      menu.hidden = false
    }
  }

  closeSortList() {
    const list = this.sortHost?.querySelector('[data-boost-sort-list]')

    if (list) {
      list.hidden = true
    }

    this.sortHost?.querySelector('[data-boost-sort-button]')?.setAttribute('aria-expanded', 'false')
  }

  renderErrorBanner() {
    if (!this.root) {
      return
    }

    let errorNode = this.root.querySelector('[data-boost-search-error]')

    if (!errorNode) {
      errorNode = document.createElement('p')
      errorNode.className = 'boost-search-results__error'
      errorNode.setAttribute('data-boost-search-error', '')
      this.root.prepend(errorNode)
    }

    errorNode.textContent = this.i18n.searchError
  }

  /**
   * Nimmt nur noch den Hinweis "Meinten Sie ...?" auf. Die Trefferzahl
   * steht in der Leiste, die Suchanfrage in der Ueberschrift darueber -
   * beides stand vorher ein zweites Mal hier.
   */
  buildSummary() {
    const summary = document.createElement('div')
    summary.className = 'boost-search-results__summary'
    return summary
  }

  async fetchDidYouMeanHint(query, requestGeneration) {
    const normalizedQuery = normalizeQuery(query)

    if (!normalizedQuery) {
      return
    }

    try {
      const response = await boostSearchClient.suggest({
        q: normalizedQuery,
        product_limit: 0,
        collection_limit: 0,
        page_limit: 0,
        suggestion_limit: 0,
        dym_limit: 1
      })

      if (requestGeneration !== this.requestGeneration) {
        return
      }

      const correctedQuery = normalizeQuery(response?.did_you_mean?.[0])

      if (!correctedQuery || correctedQuery.toLowerCase() === normalizedQuery.toLowerCase()) {
        return
      }

      this.renderDidYouMeanHint(correctedQuery)
    } catch (error) {
      if (error instanceof BoostAbortError) {
        return
      }

      console.error('Boost did-you-mean lookup failed', error)
    }
  }

  renderDidYouMeanHint(correctedQuery) {
    const summary = this.root?.querySelector('.boost-search-results__summary')

    if (!summary || summary.querySelector('.boost-search-results__did-you-mean')) {
      return
    }

    const hint = this.buildTypoHint(correctedQuery)

    if (hint) {
      summary.appendChild(hint)
    }
  }

  buildTypoHint(correctedQuery) {
    const hint = document.createElement('p')
    hint.className = 'boost-search-results__did-you-mean'

    const label = document.createElement('span')
    label.textContent = `${this.i18n.didYouMean} `
    hint.appendChild(label)

    const link = document.createElement('a')
    link.href = buildSearchPageUrl({
      filters: this.createEmptyFilterState(),
      page: DEFAULT_PAGE,
      query: correctedQuery,
      searchPath: this.searchPath,
      sort: DEFAULT_SORT
    })
    link.textContent = correctedQuery
    link.setAttribute('data-boost-search-link', '')
    link.setAttribute('data-boost-dym-link', '')
    link.setAttribute('data-query', correctedQuery)
    hint.appendChild(link)

    return hint
  }

  buildBody(response, state) {
    const wrapper = document.createElement('div')
    wrapper.className = `main-search__content ${this.gridExtraClass}`.trim()

    const activeTags = this.buildActiveTags(response, state)

    if (activeTags) {
      wrapper.appendChild(activeTags)
    }

    if (!Array.isArray(response?.products) || response.products.length === 0) {
      wrapper.appendChild(this.buildNoResults(state.query))
      return wrapper
    }

    const grid = document.createElement('div')
    grid.className = 'new-grid product-grid collection-grid boost-search-results__grid'
    grid.dataset.view = this.gridView

    response.products.forEach((product) => {
      const productCard = this.buildProductCard(product, state.query, response.meta)

      if (productCard) {
        grid.appendChild(productCard)
      }
    })

    wrapper.appendChild(grid)
    return wrapper
  }

  buildActiveTags(response, state) {
    const activeEntries = this.getActiveFilterEntries(response, state)

    if (!activeEntries.length) {
      return null
    }

    const list = document.createElement('ul')
    list.className = 'no-bullets tag-list tag-list--active-tags'

    activeEntries.forEach((entry) => {
      const item = document.createElement('li')
      item.className = 'tag tag--remove'

      const link = document.createElement('a')
      link.className = 'btn btn--small js-no-transition'
      link.href = buildSearchPageUrl({
        filters: this.removeFilterValue(state.filters, entry.filterOptionId, entry.rawValue),
        page: DEFAULT_PAGE,
        query: state.query,
        searchPath: this.searchPath,
        sort: state.sort
      })
      link.textContent = entry.label
      link.setAttribute('data-boost-filter-remove', '')
      link.setAttribute('data-filter-option-id', entry.filterOptionId)
      link.setAttribute('data-filter-value', entry.rawValue)

      item.appendChild(link)
      item.appendChild(this.getIconClone('[data-icon-close]'))
      list.appendChild(item)
    })

    const clearItem = document.createElement('li')
    clearItem.className = 'tag tag--remove'

    const clearLabel = document.createElement('span')
    clearLabel.className = 'clear_all_tag_btn btn btn--small'
    clearLabel.textContent = this.i18n.clearAll
    clearLabel.setAttribute('data-boost-filter-clear-all', '')

    clearItem.appendChild(clearLabel)
    clearItem.appendChild(this.getIconClone('[data-icon-close]'))
    list.appendChild(clearItem)

    return list
  }

  getActiveFilterEntries(response, state) {
    const optionsById = new Map(
      Array.isArray(response?.filter?.options)
        ? response.filter.options.map((option) => [option.filterOptionId, option])
        : []
    )

    return this.filterConfigs.flatMap((config) => {
      const option = optionsById.get(config.optionId)
      const activeValues = normalizeFilterValues(state.filters?.[config.optionId])

      return activeValues.map((rawValue) => {
        const matchingValue = Array.isArray(option?.values)
          ? option.values.find((value) => normalizeQuery(value?.key) === rawValue)
          : null

        return {
          filterOptionId: config.optionId,
          label: stripFacetPrefix(config.displayValue(matchingValue?.key || rawValue), config.label),
          rawValue
        }
      })
    })
  }

  buildNoResults(query) {
    const emptyState = document.createElement('div')
    emptyState.className = 'section-header boost-search-results__empty'

    const title = document.createElement('p')
    title.className = 'boost-search-results__empty-title'
    title.textContent = this.i18n.noResultsTitle
    emptyState.appendChild(title)

    const copy = document.createElement('p')
    copy.className = 'boost-search-results__empty-copy'
    copy.textContent = this.i18n.noResultsCopy.replace('{{ query }}', query)
    emptyState.appendChild(copy)

    return emptyState
  }

  getRenderableFilterOptions(response) {
    const optionsById = new Map(
      Array.isArray(response?.filter?.options)
        ? response.filter.options.map((option) => [normalizeQuery(option?.filterOptionId), option])
        : []
    )

    return this.filterConfigs.reduce((groups, config) => {
      const option = optionsById.get(config.optionId)

      if (option) {
        groups.push({ config, option })
      }

      return groups
    }, [])
  }

  renderFilterPopup(context, availableOptions, response, state) {
    const popup = this.popups[context]

    if (!popup) {
      return
    }

    popup.replaceChildren()

    if (!availableOptions.length) {
      popup.hidden = true
      return
    }

    popup.hidden = false

    // Kopfzeile, Facettenliste, Werteseiten und Fusszeile - dasselbe
    // Markup wie im Panel der Kollektionsseite (snippets/item-grid.liquid),
    // damit es aus components.css dieselbe Gestaltung bekommt.
    popup.appendChild(this.buildPopupHeader())

    const body = document.createElement('div')
    body.className = 'custom-popup__mobilebody'

    const tabs = document.createElement('ul')
    tabs.id = 'tabs-acc'
    tabs.className = 'tabsacc'

    const inner = document.createElement('div')
    inner.className = 'tabs-acc__inner'

    const form = document.createElement('form')
    form.className = 'popup-filter-form'
    form.addEventListener('submit', (event) => event.preventDefault())

    availableOptions.forEach(({ config, option }) => {
      tabs.appendChild(this.buildPopupTab(config))
      form.appendChild(this.buildPopupPanel(option, config, state, context))
    })

    inner.appendChild(form)
    body.appendChild(tabs)
    body.appendChild(inner)
    popup.appendChild(body)
    popup.appendChild(this.buildPopupFooter(context, response))

    // Bewusst ohne vorgewaehlte Facette: das Panel oeffnet mit der Liste
    // der Facettennamen, eine Zeile fuehrt zu ihren Werten, der Pfeil
    // zurueck. Das Tab-Nebeneinander von Boost passte auf kein Telefon.
    this.setPopupTab(popup, '')
    this.syncPopupSelections()
  }

  buildPopupHeader() {
    const header = document.createElement('div')
    header.className = 'custom-mobile__popupfilterheader'

    const title = document.createElement('h2')
    title.textContent = this.i18n.filter
    header.appendChild(title)

    header.appendChild(this.buildCloseIcon())

    return header
  }

  buildCloseIcon() {
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    icon.setAttribute('class', 'close-icom')
    icon.setAttribute('height', '40px')
    icon.setAttribute('width', '40px')
    icon.setAttribute('viewBox', '0 -960 960 960')
    icon.setAttribute('fill', '#000000')
    icon.setAttribute('role', 'button')
    icon.setAttribute('aria-label', this.dataset.closeLabel || 'Close')
    icon.setAttribute('data-boost-filter-popup-close', '')

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute(
      'd',
      'm251.33-204.67-46.66-46.66L433.33-480 204.67-708.67l46.66-46.66L480-526.67l228.67-228.66 46.66 46.66L526.67-480l228.66 228.67-46.66 46.66L480-433.33 251.33-204.67Z'
    )
    icon.appendChild(path)

    return icon
  }

  buildPopupTab(config) {
    const item = document.createElement('li')
    item.setAttribute('data-list-tab-id', config.tabId)
    item.setAttribute('data-boost-filter-tab', '')
    item.setAttribute('data-tab-id', config.tabId)

    const trigger = document.createElement('span')
    trigger.className = 'tab-link'
    trigger.setAttribute('data-tab-id', config.tabId)
    trigger.appendChild(document.createTextNode(config.label))

    const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    chevron.setAttribute('height', '24px')
    chevron.setAttribute('width', '24px')
    chevron.setAttribute('viewBox', '0 -960 960 960')
    chevron.setAttribute('fill', '#000000')
    chevron.setAttribute('aria-hidden', 'true')
    chevron.setAttribute('focusable', 'false')
    const chevronPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    chevronPath.setAttribute('d', 'm321-80-71-71 329-329-329-329 71-71 400 400L321-80Z')
    chevron.appendChild(chevronPath)
    trigger.appendChild(chevron)

    item.appendChild(trigger)

    return item
  }

  buildPopupPanel(option, config, state, context) {
    const panel = document.createElement('div')
    panel.className = 'tabs-contents'
    panel.setAttribute('data-list-targeted-tab-id', config.tabId)
    panel.setAttribute('data-targeted-tab-id', config.tabId)

    const back = document.createElement('div')
    back.className = 'tabs-acc__backbtn'

    const backIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    backIcon.setAttribute('class', 'backbtn')
    backIcon.setAttribute('height', '24px')
    backIcon.setAttribute('width', '24px')
    backIcon.setAttribute('viewBox', '0 -960 960 960')
    backIcon.setAttribute('fill', '#000000')
    backIcon.setAttribute('role', 'button')
    backIcon.setAttribute('data-boost-filter-back', '')
    const backPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    backPath.setAttribute('d', 'M400-80 0-480l400-400 71 71-329 329 329 329-71 71Z')
    backIcon.appendChild(backPath)
    back.appendChild(backIcon)

    const backLabel = document.createElement('span')
    backLabel.textContent = config.label
    back.appendChild(backLabel)

    back.appendChild(this.buildCloseIcon())
    panel.appendChild(back)

    const list = document.createElement('ul')
    list.className = 'no-bullets tag-list'
    panel.appendChild(list)

    const activeValues = normalizeFilterValues(state.filters?.[option.filterOptionId])

    option.values.forEach((value, valueIndex) => {
      const rawValue = normalizeQuery(value?.key)

      if (!rawValue) {
        return
      }

      const active = activeValues.includes(rawValue)
      const item = document.createElement('li')
      item.className = active ? 'tag tag--active' : 'tag'
      item.dataset.docCount = String(Number(value?.doc_count) || 0)

      if (!active && Number(value?.doc_count) === 0) {
        item.classList.add('hide')
      }

      const label = document.createElement('label')
      label.className = 'tag__checkbox-wrapper text-label js_popup_filter_list'
      label.setAttribute(
        'for',
        `tagInput-popup-filter-${sanitizeIdentifier(option.filterOptionId)}-${context}-${valueIndex + 1}`
      )
      item.appendChild(label)

      const input = document.createElement('input')
      input.id = label.getAttribute('for')
      input.type = 'checkbox'
      input.className = 'tag__input'
      input.name = `${option.filterOptionId}[]`
      input.value = rawValue
      input.checked = active
      input.setAttribute('data-boost-filter-input', '')
      input.setAttribute('data-filter-option-id', option.filterOptionId)
      label.appendChild(input)

      const checkbox = document.createElement('span')
      checkbox.className = 'tag__checkbox'
      label.appendChild(checkbox)

      const textWrap = document.createElement('span')
      const text = document.createElement('span')
      text.className = 'tag__text'
      text.textContent = stripFacetPrefix(config.displayValue(rawValue), config.label)
      textWrap.appendChild(text)
      textWrap.appendChild(document.createTextNode(` (${Number(value?.doc_count) || 0})`))
      label.appendChild(textWrap)
      list.appendChild(item)
    })

    return panel
  }

  buildPopupFooter(context, response) {
    const footer = document.createElement('div')
    footer.className = context === 'mobile' ? 'custom-popup__footermobile' : 'custom-popup__footer'

    const resultCount = document.createElement('div')

    if (context === 'mobile') {
      resultCount.className = 'custom-popup__results'
      const resultText = document.createElement('p')
      resultText.textContent = this.getResultsCountText(response?.total_product)
      resultCount.appendChild(resultText)
    } else {
      resultCount.textContent = this.getResultsCountText(response?.total_product)
    }

    footer.appendChild(resultCount)

    const buttonWrap = document.createElement('div')
    buttonWrap.className = 'custom-popup__button'

    const button = document.createElement('button')
    button.type = 'button'
    button.disabled = true
    button.textContent = this.i18n.applyFilters
    button.setAttribute('data-boost-filter-apply', '')
    buttonWrap.appendChild(button)
    footer.appendChild(buttonWrap)

    return footer
  }

  /**
   * Blaettert im Panel zwischen der Facettenliste und den Werten einer
   * Facette. Leere tabId heisst: zurueck zur Liste. Gesteuert wird das
   * ueber die Klasse "active" - genau die, auf die components.css und
   * das Panel der Kollektionsseite hoeren.
   */
  setPopupTab(popup, tabId) {
    if (!popup) {
      return
    }

    popup.dataset.activeTabId = tabId || ''

    popup.querySelectorAll('.tabsacc > li').forEach((item) => {
      item.classList.toggle('active', Boolean(tabId) && item.getAttribute('data-list-tab-id') === tabId)
    })

    popup.querySelectorAll('.tabs-contents').forEach((panel) => {
      panel.classList.toggle(
        'active',
        Boolean(tabId) && panel.getAttribute('data-list-targeted-tab-id') === tabId
      )
    })
  }

  updateFilterTriggerLabels(filters) {
    const count = this.getAppliedFilterCount(filters)
    const label = `${this.i18n.filter} (${count})`

    ;[this.desktopFilterTrigger, this.mobileFilterTrigger].forEach((trigger) => {
      const labelNode = trigger?.querySelector('[data-boost-filter-trigger-label]')

      if (labelNode) {
        labelNode.textContent = label
      }
    })
  }

  getAppliedFilterCount(filters) {
    if (!this.filterConfigs.length) {
      return Object.values(filters || {}).reduce((count, values) => {
        return count + normalizeFilterValues(values).length
      }, 0)
    }

    return this.filterConfigs.reduce((count, config) => {
      return count + normalizeFilterValues(filters?.[config.optionId]).length
    }, 0)
  }

  syncPopupSelections() {
    const sourceFilters = this.pendingFilters || this.currentState?.filters || this.createEmptyFilterState()

    this.querySelectorAll('[data-boost-filter-input]').forEach((input) => {
      const filterOptionId = input.getAttribute('data-filter-option-id') || ''
      const activeValues = normalizeFilterValues(sourceFilters?.[filterOptionId])
      const isChecked = activeValues.includes(normalizeQuery(input.value))
      const tag = input.closest('.tag')

      input.checked = isChecked

      if (tag) {
        tag.classList.toggle('tag--active', isChecked)

        if (!isChecked && Number.parseInt(tag.dataset.docCount || '0', 10) === 0) {
          tag.classList.add('hide')
        } else {
          tag.classList.remove('hide')
        }
      }
    })

    const isDirty = this.popupSnapshotFilters && this.pendingFilters
      ? !filtersAreEqual(this.popupSnapshotFilters, this.pendingFilters, this.filterConfigs)
      : false

    this.querySelectorAll('[data-boost-filter-apply]').forEach((button) => {
      button.disabled = !isDirty
    })
  }

  getIconClone(selector) {
    const template = this.querySelector(selector)

    if (!template || !(template instanceof HTMLTemplateElement)) {
      return document.createDocumentFragment()
    }

    return template.content.cloneNode(true)
  }

  updateFilterValue(filters, filterOptionId, rawValue, isSelected) {
    const nextFilters = this.normalizeFilters(filters || this.createEmptyFilterState())
    const currentValues = nextFilters[filterOptionId] || []
    const normalizedValue = normalizeQuery(rawValue)

    if (!normalizedValue) {
      return nextFilters
    }

    nextFilters[filterOptionId] = isSelected
      ? normalizeFilterValues([...currentValues, normalizedValue])
      : currentValues.filter((value) => value !== normalizedValue)

    return nextFilters
  }

  removeFilterValue(filters, filterOptionId, rawValue) {
    return this.updateFilterValue(filters, filterOptionId, rawValue, false)
  }

  buildBoostFilterParams(filters) {
    return Object.entries(filters || {}).reduce((params, [optionId, values]) => {
      const normalizedOptionId = normalizeQuery(optionId)
      const normalizedValues = normalizeFilterValues(values)

      if (normalizedOptionId.startsWith('pf_') && normalizedValues.length) {
        params[`${normalizedOptionId}[]`] = normalizedValues
      }

      return params
    }, {})
  }

  openPopup(context, trigger) {
    const popup = this.popups[context]
    const shell = this.popupShells[context]

    if (!popup || !shell || popup.hidden) {
      return
    }

    if (this.popupOpen) {
      return
    }

    this.popupOpen = true
    this.popupContext = context
    this.popupTrigger = trigger || null
    this.closeFacetMenus()
    this.closeSortList()
    // Das Panel oeffnet immer mit der Facettenliste, nicht dort, wo man
    // es zuletzt verlassen hat.
    this.setPopupTab(popup, '')
    this.popupSnapshotFilters = this.normalizeFilters(this.currentState?.filters || this.createEmptyFilterState())
    this.pendingFilters = this.normalizeFilters(this.currentState?.filters || this.createEmptyFilterState())
    shell.hidden = false
    popup.classList.add('active')
    popup.hidden = false

    if (context === 'mobile') {
      document.body.classList.add('mobile-filter-open')
    } else {
      document.body.classList.add('filter-popup__open')
    }

    lockScroll()
    this.syncPopupSelections()

    window.setTimeout(() => {
      trapFocus(popup, {
        elementToFocus: popup.querySelector('[data-boost-filter-popup-close]') || popup
      })
    }, 0)
  }

  closePopup({ restoreFocus = true, discardPending = true } = {}) {
    if (!this.popupOpen && this.popupContext === null) {
      return
    }

    if (discardPending) {
      this.pendingFilters = this.normalizeFilters(this.currentState?.filters || this.createEmptyFilterState())
      this.syncPopupSelections()
    }

    Object.entries(this.popupShells).forEach(([context, shell]) => {
      const popup = this.popups[context]

      if (popup) {
        popup.classList.remove('active')
      }

      if (shell) {
        shell.hidden = true
      }
    })

    this.popupOpen = false
    this.popupContext = null
    this.popupSnapshotFilters = null
    removeTrapFocus()
    unlockScroll()
    document.body.classList.remove('filter-popup__open', 'mobile-filter-open')

    if (restoreFocus && this.popupTrigger) {
      this.popupTrigger.focus()
    }

    this.popupTrigger = null
  }

  applyPendingFilters() {
    const filters = this.normalizeFilters(this.pendingFilters || this.createEmptyFilterState())
    this.closePopup({ restoreFocus: false, discardPending: false })
    this.pendingFilters = null

    this.fetchAndRender(
      {
        ...this.currentState,
        filters,
        page: DEFAULT_PAGE
      },
      {
        historyMode: 'push'
      }
    )
  }

  buildProductCard(product, query, meta) {
    if (!product || typeof product !== 'object') {
      return null
    }

    const variant = selectVariant(product.variants, query)
    const available = isAvailable(product, variant)
    const url = buildResourceUrl('products', product.handle, this.routesRoot)
    const title = normalizeQuery(product.title) || this.i18n.untitledProduct
    const card = document.createElement('div')

    card.className = 'grid-item grid-product grid-product-image-breathing-room--false'
    card.setAttribute('data-card-height', '')

    const productItem = document.createElement('div')
    productItem.className = 'product-grid-item'
    productItem.dataset.productHandle = getValidHandle(product.handle)
    productItem.dataset.productId = String(product.id || '')
    productItem.setAttribute('data-product-grid-item', '')
    card.appendChild(productItem)

    const content = document.createElement('div')
    content.className = 'grid-item__content'
    productItem.appendChild(content)

    content.appendChild(this.buildProductMedia(url, title, product, variant, meta, available))
    content.appendChild(this.buildProductMeta(url, title, variant, meta, product))

    return card
  }

  buildProductMedia(url, title, product, variant, meta, available) {
    const imageWrap = document.createElement('div')
    imageWrap.className = 'grid-product__image-wrap'

    const ratioBox = document.createElement('div')
    ratioBox.className = 'grid__image-ratio grid__image-ratio--square'
    imageWrap.appendChild(ratioBox)

    const imageInfo = Array.isArray(product?.images_info) ? product.images_info[0] : null

    if (imageInfo?.src) {
      const image = document.createElement('img')
      image.src = imageInfo.src
      image.alt = imageInfo.alt || title || this.i18n.productImageAlt
      image.loading = 'lazy'
      image.className = 'grid__image-contain'

      if (imageInfo.width) {
        image.width = imageInfo.width
      }

      if (imageInfo.height) {
        image.height = imageInfo.height
      }

      ratioBox.appendChild(image)
    } else {
      const placeholder = document.createElement('div')
      placeholder.className = 'boost-search-results__image-placeholder'
      placeholder.textContent = this.i18n.noImage
      ratioBox.appendChild(placeholder)
    }

    /* HORANDO 13.09.2026 - "Sie sparen"-Badge entfernt. Eine Auszeichnung, die
       fast jede Kachel traegt, zeichnet nichts mehr aus; der durchgestrichene
       UVP neben dem Preis sagt dasselbe ruhiger. Auf der Kollektionsseite ist
       der Badge seit demselben Tag raus (product-grid-item.liquid). */

    /* HORANDO 13.09.2026 - "Sofort verfuegbar" oben links im Bild, wie auf der
       Kollektionskachel. Als sofort verfuegbar gilt allein "1-3d" - Konvention
       des Themes, global.immediately_available ist kein verlaesslicher
       Lagerbeleg. Gestaltung kommt aus snippets/plp-delivery.liquid. */
    /* 24.09.2026 - gleiche Auszeichnung wie snippets/hd-product-badges.liquid:
       1-3d gefuellt "Sofort lieferbar", 3-7d hell "Schnell lieferbar".
       Gestaltung aus assets/hd-plp-kachel.css. */
    const badgeDelivery = this.getProductDelivery(product)
    const badgeKind = badgeDelivery === '1-3d' ? 'stock' : badgeDelivery === '3-7d' ? 'fast' : ''

    if (badgeKind && available && !this.isProductConcierge(product)) {
      const availabilityOverlay = document.createElement('div')
      availabilityOverlay.className = badgeKind === 'stock'
        ? 'hd-badge hd-badge--stock hd-badge--top'
        : 'hd-badge hd-badge--light hd-badge--top'

      const availabilityDot = document.createElement('span')
      availabilityDot.className = 'hd-badge__dot'
      availabilityDot.setAttribute('aria-hidden', 'true')
      availabilityOverlay.appendChild(availabilityDot)

      const availabilityText = document.createElement('span')
      availabilityText.textContent = badgeKind === 'stock' ? this.i18n.immediatelyAvailable : this.i18n.fastDelivery
      availabilityOverlay.appendChild(availabilityText)

      imageWrap.appendChild(availabilityOverlay)
    }

    if (!available && !this.isProductConcierge(product)) {
      const tags = document.createElement('div')
      tags.className = 'grid-product__tags'

      const soldOutTag = document.createElement('div')
      soldOutTag.className = 'grid-product__tag grid-product__tag--sold-out'
      soldOutTag.textContent = this.i18n.soldOut

      tags.appendChild(soldOutTag)
      imageWrap.appendChild(tags)
    }

    /* HORANDO 14.09.2026 - Concierge-Auszeichnung unten rechts im Bild, wie
       auf der Liquid-Kachel (snippets/product-grid-item.liquid). Die
       Gestaltung steht in snippets/section-main-search.liquid, weil der
       dortige style-Block auf der Suchergebnisseite ohnehin ausgegeben wird -
       das Liquid-Snippet rendert hier nicht und brachte seine eigenen Regeln
       nicht mit. */
    if (this.conciergeBadgeLabel && this.isProductConcierge(product)) {
      const conciergeBadge = document.createElement('div')
      conciergeBadge.className = 'product-tile__concierge-badge'
      conciergeBadge.textContent = this.conciergeBadgeLabel

      imageWrap.appendChild(conciergeBadge)
    }

    /* HORANDO 14.09.2026 - CPO-Auszeichnung unten rechts im Bild, wie auf der
       Liquid-Kachel. Gleiche Masse wie die Concierge-Auszeichnung; die Regeln
       stehen ebenfalls in snippets/section-main-search.liquid. Traegt eine
       Kachel beide Balken, ruecken sie uebereinander. "CPO" ist eine feste
       Zeichenkette und wird nicht uebersetzt. */
    if (this.isProductCertifiedPreOwned(product)) {
      const cpoBadge = document.createElement('div')
      cpoBadge.className = this.isProductConcierge(product)
        ? 'product-tile__cpo-badge product-tile__cpo-badge--stacked'
        : 'product-tile__cpo-badge'
      cpoBadge.textContent = 'CPO'

      imageWrap.appendChild(cpoBadge)
    }

    if (url) {
      const link = document.createElement('a')
      link.className = 'grid-item__link'
      link.href = url
      link.appendChild(imageWrap)
      return link
    }

    return imageWrap
  }

  buildProductMeta(url, title, variant, meta, product) {
    const metaWrapper = document.createElement('div')
    metaWrapper.className = 'grid-item__meta'

    const main = document.createElement('div')
    main.className = 'grid-item__meta-main'

    const titleNode = document.createElement(url ? 'a' : 'div')
    titleNode.className = 'grid-product__title'
    titleNode.textContent = title

    if (url) {
      titleNode.href = url
    }

    main.appendChild(titleNode)
    metaWrapper.appendChild(main)

    const secondary = document.createElement('div')
    secondary.className = 'grid-item__meta-secondary'

    const price = document.createElement('div')
    price.className = 'grid-product__price'

    /* HORANDO 14.09.2026 - Concierge-Uhren zeigen keinen Preis. Auf der
       Liquid-Kachel umschliesst {% if custom.concierge %} den gesamten
       Preisblock und laesst ihn leer (snippets/product-grid-item.liquid);
       hier dasselbe Verhalten. Diese Uhren tragen einen Platzhalterpreis -
       zum Beispiel 111.111,00 EUR bei 26331IP.OO.1220IP.01 -, der nach aussen
       nie erscheinen darf. Der leere Kasten bleibt stehen, damit Kachelhoehe
       und Abstand zur Lieferzeile gleich bleiben. */
    if (!this.isProductConcierge(product)) {
      const priceValue = document.createElement('span')
      priceValue.className = 'grid-product__price--current price-black'
      priceValue.textContent = this.getProductPriceText(variant, meta)
      price.appendChild(priceValue)

      /* HORANDO 13.09.2026 - UVP durchgestrichen neben dem Preis, wie auf der
         Kollektionskachel. */
      /* HORANDO 19.09.2026 - ausser bei Rolex, Audemars Piguet und Patek
         Philippe: dort wird der UVP gar nicht mehr gezeigt, siehe
         shouldShowUvp() weiter unten. */
      const compareAtAmount = priceToCents(variant?.compare_at_price)
      const priceAmount = priceToCents(variant?.price)

      if (
        this.shouldShowUvp(product) &&
        Number.isFinite(compareAtAmount) &&
        Number.isFinite(priceAmount) &&
        compareAtAmount > priceAmount
      ) {
        const originalPrice = document.createElement('span')
        originalPrice.className = 'grid-product__price--original'
        originalPrice.textContent = `${this.i18n.uvp} ${formatMoneyAmount(compareAtAmount, meta, this.moneyFormat)}`
        price.appendChild(originalPrice)
      }
    }

    secondary.appendChild(price)

    const deliveryNode = this.buildDeliveryNode(this.getProductDelivery(product), this.isProductConcierge(product))

    if (deliveryNode) {
      secondary.appendChild(deliveryNode)
    }

    metaWrapper.appendChild(secondary)

    const sku = typeof variant?.sku === 'string' ? variant.sku.trim() : ''
    const year = this.shouldShowYear(product) ? this.getProductYear(product) : ''

    if (sku || year) {
      const bottomWrap = document.createElement('div')
      bottomWrap.className = 'grid_bottom_main_wrap'

      if (sku) {
        const referenceWrap = document.createElement('div')
        referenceWrap.className = 'grid-item__meta-third'

        const referenceValue = document.createElement('div')
        referenceValue.className = 'grid-product__vendor'
        referenceValue.textContent = sku

        referenceWrap.appendChild(referenceValue)
        bottomWrap.appendChild(referenceWrap)
      }

      if (year) {
        const yearWrap = document.createElement('div')
        yearWrap.className = 'grid-item__meta-fourth'

        const yearText = document.createElement('p')
        yearText.appendChild(document.createTextNode(this.i18n.year))
        yearText.appendChild(document.createTextNode(': '))
        yearText.appendChild(document.createTextNode(year))

        yearWrap.appendChild(yearText)
        bottomWrap.appendChild(yearWrap)
      }

      metaWrapper.appendChild(bottomWrap)
    }

    return metaWrapper
  }

  /* HORANDO 19.09.2026 - UVP-Anzeige: dieselbe Regel wie in
     snippets/hd-show-uvp.liquid, dort steht die Begruendung. Kurz: bei
     Audemars Piguet, Rolex und Patek Philippe ist der hinterlegte
     Streichpreis kein Hersteller-Listenpreis, sondern liegt knapp ueber dem
     Verkaufspreis - er wird deshalb nirgends mehr gezeigt. Wer die Regel
     aendert, muss das Liquid-Snippet und assets/quick-search-overlay.js
     nachziehen. Fehlt der Vendor in der Antwort, bleibt es beim UVP. */
  shouldShowUvp(product) {
    return !UVP_HIDDEN_VENDORS.includes(normalizeQuery(product?.vendor))
  }

  /* HORANDO 14.09.2026 - Jahresangabe: dieselbe Regel wie in
     snippets/show-year.liquid, dort steht die Begruendung. Kurz: Concierge-
     Uhren zeigen kein Jahr, und bei Audemars Piguet, Rolex und Patek Philippe
     nur, solange die Uhr lagernd ist ("1-3d", Quelle wie bei der Lieferzeile).
     Wer die Regel aendert, muss beide Stellen nachziehen. Fehlt der Vendor in
     der Antwort, bleibt es beim Jahr - lieber eine Angabe zu viel als eine
     Marke faelschlich stumm zu schalten. */
  shouldShowYear(product) {
    if (this.isProductConcierge(product)) {
      return false
    }

    if (!YEAR_ON_STOCK_ONLY_VENDORS.includes(normalizeQuery(product?.vendor))) {
      return true
    }

    return this.getProductDelivery(product) === '1-3d'
  }

  getProductYear(product) {
    const metafields = Array.isArray(product?.metafields) ? product.metafields : []
    const yearMetafield = metafields.find((metafield) =>
      metafield &&
      metafield.namespace === 'global' &&
      metafield.key === 'Jahr' &&
      normalizeQuery(metafield.value)
    )

    return yearMetafield ? normalizeQuery(yearMetafield.value) : ''
  }

  /* HORANDO 13.09.2026 - Lieferzeit auf der Suchkachel, gleiche Zeile wie auf
     Kollektionsseite, Startseite und im Warenkorb (snippets/plp-delivery.liquid).
     Quelle ist custom.new_delivery, nicht global.deliverytime: Letzteres ist je
     Sprache uebersetzt und veraltet, sobald sich der deutsche Wert aendert.
     Als sofort verfuegbar gilt allein "1-3d" - Konvention des Themes. */
  /* HORANDO 14.09.2026 - Concierge-Uhren tragen kein "Ausverkauft" (siehe
     snippets/product-grid-item.liquid). Der Suchtreffer liest dasselbe
     Metafeld wie die Kachel, damit Suche und Kollektionsseite nicht
     auseinander laufen. */
  isProductConcierge(product) {
    const metafields = Array.isArray(product?.metafields) ? product.metafields : []

    return metafields.some((metafield) => {
      if (!metafield || metafield.namespace !== 'custom' || metafield.key !== 'concierge') {
        return false
      }

      /* HORANDO 14.09.2026 - concierge ist ein boolean-Metafeld. Die uebrigen
         hier gelesenen Felder (custom.new_delivery, global.Jahr) sind Text,
         bei denen steht im Index genau der Wert. Ein boolean kann dagegen als
         echtes true, als "true" oder als "1" ankommen - je nachdem, wie der
         Suchindex es abgelegt hat. Alle drei Schreibweisen zaehlen, sonst
         faellt die Uhr still in den Normalfall zurueck und zeigt Preis,
         Lieferzeit und "Ausverkauft". */
      const value = normalizeQuery(metafield.value).toLowerCase()

      return value === 'true' || value === '1'
    })
  }

  /* HORANDO 14.09.2026 - CPO-Kennzeichnung auf der Suchkachel. Quelle ist
     global.certified_pre_owned, dasselbe Feld wie in
     snippets/product-grid-item.liquid. Wie bei custom.concierge ist es ein
     boolean und kann im Suchindex als true, "true" oder "1" liegen. */
  isProductCertifiedPreOwned(product) {
    const metafields = Array.isArray(product?.metafields) ? product.metafields : []

    return metafields.some((metafield) => {
      if (!metafield || metafield.namespace !== 'global' || metafield.key !== 'certified_pre_owned') {
        return false
      }

      const value = normalizeQuery(metafield.value).toLowerCase()

      return value === 'true' || value === '1'
    })
  }

  getProductDelivery(product) {
    const metafields = Array.isArray(product?.metafields) ? product.metafields : []
    const pick = (namespace, key) => {
      const found = metafields.find((metafield) =>
        metafield && metafield.namespace === namespace && metafield.key === key && normalizeQuery(metafield.value)
      )

      return found ? normalizeQuery(found.value) : ''
    }

    return pick('custom', 'new_delivery') || pick('custom', 'new_delivery')
  }

  /* HORANDO 14.09.2026 - Concierge-Uhren zeigen keine Lieferzeit, sondern
     "Auf Anfrage" - gleicher Aufbau und gleiche Klasse wie auf der
     Kollektionskachel (snippets/plp-delivery.liquid), damit die Gestaltung
     aus plp-delivery-styles.liquid greift. Die Pruefung steht vor der
     Leerpruefung, damit die Zeile auch bei einer Concierge-Uhr ohne
     Lieferzeit-Metafeld erscheint. */
  buildDeliveryNode(rawDelivery, isConcierge) {
    if (isConcierge) {
      const requestWrapper = document.createElement('div')
      requestWrapper.className = 'plp-delivery plp-delivery--request'

      const requestDot = document.createElement('span')
      requestDot.className = 'plp-delivery__dot'
      requestDot.setAttribute('aria-hidden', 'true')
      requestWrapper.appendChild(requestDot)

      const requestLabel = document.createElement('span')
      requestLabel.className = 'plp-delivery__text'
      requestLabel.textContent = this.i18n.deliveryOnRequest
      requestWrapper.appendChild(requestLabel)

      return requestWrapper
    }

    if (!rawDelivery) {
      return null
    }

    const letters = rawDelivery.replace(/[0-9\-\/\s]/g, '')
    let text = rawDelivery

    if (letters === 'w' || letters === 'd') {
      const unit = letters === 'w' ? this.i18n.deliveryWeeks : this.i18n.deliveryDays
      text = `${rawDelivery.replace(/[wd]/g, '').trim()} ${unit}`
    }

    const wrapper = document.createElement('div')
    wrapper.className = (rawDelivery === '1-3d' ? 'plp-delivery plp-delivery--stock' : 'plp-delivery') +
      (letters === 'w' ? ' plp-delivery--weeks' : letters === 'd' ? ' plp-delivery--days' : '')

    const dot = document.createElement('span')
    dot.className = 'plp-delivery__dot'
    dot.setAttribute('aria-hidden', 'true')
    wrapper.appendChild(dot)

    const label = document.createElement('span')
    label.className = 'plp-delivery__text'
    label.appendChild(document.createTextNode(`${this.i18n.delivery} `))

    const separator = document.createElement('span')
    separator.className = 'plp-delivery__sep'
    separator.setAttribute('aria-hidden', 'true')
    separator.textContent = '·'
    label.appendChild(separator)
    label.appendChild(document.createTextNode(` ${text}`))

    wrapper.appendChild(label)

    return wrapper
  }

  getProductPriceText(variant, meta) {
    const amount = priceToCents(variant?.price)

    if (amount === null) {
      return this.i18n.priceUnavailable
    }

    return formatMoneyAmount(amount, meta, this.moneyFormat)
  }

  buildPagination(response, state) {
    const totalProducts = Number.parseInt(response?.total_product, 10)
    const totalPages = Math.max(DEFAULT_PAGE, Math.ceil(totalProducts / this.pageSize))

    if (!Number.isFinite(totalPages) || totalPages <= DEFAULT_PAGE) {
      return document.createDocumentFragment()
    }

    const pagination = document.createElement('div')
    pagination.className = 'pagination boost-search-results__pagination'

    if (state.page > DEFAULT_PAGE) {
      pagination.appendChild(this.buildPaginationLink({
        className: 'prev',
        label: this.i18n.previous,
        page: state.page - 1
      }))
    }

    getPaginationParts(state.page, totalPages).forEach((part) => {
      if (part.type === 'gap') {
        const gap = document.createElement('span')
        gap.className = 'page'
        gap.textContent = '…'
        pagination.appendChild(gap)
        return
      }

      const pageElement = document.createElement('span')
      pageElement.className = part.page === state.page ? 'page current' : 'page'

      if (part.page === state.page) {
        pageElement.textContent = String(part.page)
      } else {
        const link = document.createElement('a')
        link.href = buildSearchPageUrl({
          filters: state.filters,
          page: part.page,
          query: state.query,
          searchPath: this.searchPath,
          sort: state.sort
        })
        link.textContent = String(part.page)
        link.setAttribute('data-boost-search-link', '')
        link.setAttribute('data-boost-page-link', '')
        link.setAttribute('data-page', String(part.page))
        pageElement.appendChild(link)
      }

      pagination.appendChild(pageElement)
    })

    if (state.page < totalPages) {
      pagination.appendChild(this.buildPaginationLink({
        className: 'next',
        label: this.i18n.next,
        page: state.page + 1
      }))
    }

    return pagination
  }

  buildPaginationLink({ className, label, page }) {
    const wrapper = document.createElement('span')
    wrapper.className = className

    const link = document.createElement('a')
    link.href = buildSearchPageUrl({
      filters: this.currentState.filters,
      page,
      query: this.currentState.query,
      searchPath: this.searchPath,
      sort: this.currentState.sort
    })
    link.textContent = label
    link.setAttribute('data-boost-search-link', '')
    link.setAttribute('data-boost-page-link', '')
    link.setAttribute('data-page', String(page))
    wrapper.appendChild(link)

    return wrapper
  }

  getResultsCountText(count) {
    const safeCount = Number.parseInt(count, 10) || 0
    const countTemplate = safeCount === 1 ? this.i18n.resultsCountOne : this.i18n.resultsCountOther
    return countTemplate.replace('__count__', String(safeCount))
  }

  getResultsSummaryText(count, query) {
    const countLabel = this.getResultsCountText(count)
    const queryLabel = this.i18n.forQuery.replace('{{ query }}', query)

    return `${countLabel} ${queryLabel}`.trim()
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('boost-search-results')) {
  customElements.define('boost-search-results', BoostSearchResults)
}

export {
  BoostSearchResults,
  DEFAULT_PAGE,
  DEFAULT_SORT,
  SORT_OPTIONS
}
